import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, SelectPicker, Input} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import { AxiosError } from 'axios';
import ImageUploader from '@/components/ImageUploader';
import { MdFlag } from 'react-icons/md';
import { AdvanceShareStyle, AdvanceShareStyleCategoryList, AdvanceShareStyleStatusList, UpsertAdvanceShareStyle } from '@/models/advance-share-style.model';
import { upsertAdvanceShareStyle } from '@/services/advance-share-style.service';
import ErrorMessage from '@/components/ErrorMessage';

const schema = Schema.Model<UpsertAdvanceShareStyle>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  coverImageUrl: Schema.Types.StringType(),
  category: Schema.Types.NumberType().isRequired('This field is required.'),
});


const emptyModel: UpsertAdvanceShareStyle = {
  id: undefined,
  name: undefined,
  coverImageUrl: undefined,
  category: undefined,
};

interface AdvanceShareStyleFormProps{
  formObject?: AdvanceShareStyle;
  pageState: PageState
};

const AdvanceShareStyleForm: React.FC<AdvanceShareStyleFormProps> = ({formObject, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertAdvanceShareStyle>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, });
      return;
    }

    if (formObject) {
      const castedModel: UpsertAdvanceShareStyle = { 
        ...formObject, 
      };

      setFormValue(castedModel);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<any> = await upsertAdvanceShareStyle(formValue);
      if (result.success) {
        nav('/advance-shares/styles');
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
     <Form name='advance-share-style-form' fluid
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={schema}
        disabled={loading}
      >
        <Grid w='100%' fluid >
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Cover Image</Form.Label>
                <Form.Control
                  accepter={ImageUploader}
                  name="coverImageUrl"
                  defaultIcon={MdFlag}
                  height="250px"
                  url={formValue.coverImageUrl}
                  objectStorageCategory={ObjectStorageFileCategory.AdvanceShare}
                  onChange={url => setFormValue(pre => ({...pre, coverImageUrl: url}))}              
                />
              </Form.Group>
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control name="name" value={formValue.name ?? ''}/>
              </Form.Group>
            </Col>
          </Row> 
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Category</Form.Label>
                <Form.Control name="category" accepter={SelectPicker} block
                  data={AdvanceShareStyleCategoryList}
                />                   
              </Form.Group>                 
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col span={{xs:24, xl:6}} >
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Input value={AdvanceShareStyleStatusList.findLast(s => s.value == formObject?.status)?.label} disabled/>                   
              </Form.Group>                 
            </Col>
            <Col span={{xs:24, xl:6}} >
              <Form.Group>
                <Form.Label>Current Version</Form.Label>
                <Input value={formObject?.currentVersionNo} disabled/>                   
              </Form.Group>                 
            </Col>
          </Row>
        </Grid>
      </Form>

      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
      
      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default AdvanceShareStyleForm;

